// Daily per-user tally of items sorted, used to fire "every 100 items"
// achievement popups. Local-only (no server round-trip) — it only needs to know
// when a 100-boundary is crossed within the day; the authoritative productivity
// numbers live in the points / scan logs server-side. Keyed by uid + date so a
// shared handheld doesn't bleed one operator's tally into the next.

export const DAILY_ITEMS_KEY = "gamification-daily-items";
export const MILESTONE_STEP = 100;

interface StoredItems {
    uid: number;
    date: string;
    count: number;
}

const todayKey = (): string => {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
};

const currentUid = (): number | null => {
    try {
        const raw = window.localStorage.getItem("user");
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        const uid = Number(parsed?.uid);
        return Number.isFinite(uid) && uid > 0 ? uid : null;
    } catch {
        return null;
    }
};

const readStored = (): StoredItems | null => {
    try {
        const raw = window.localStorage.getItem(DAILY_ITEMS_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as StoredItems;
        if (
            typeof parsed?.uid !== "number" ||
            typeof parsed?.date !== "string" ||
            typeof parsed?.count !== "number"
        ) return null;
        return parsed;
    } catch {
        return null;
    }
};

const writeStored = (data: StoredItems) => {
    try {
        window.localStorage.setItem(DAILY_ITEMS_KEY, JSON.stringify(data));
    } catch {
        // localStorage may not be available
    }
};

export const getItemCount = (): number => {
    const stored = readStored();
    if (!stored) return 0;
    const uid = currentUid();
    if (uid === null || stored.uid !== uid) return 0;
    if (stored.date !== todayKey()) return 0;
    return stored.count > 0 ? stored.count : 0;
};

// Record one sorted item against today's tally. Returns the new total and, when
// this item tips the tally past a 100 boundary, the milestone just reached
// (e.g. 100, 200, …) so the caller can celebrate it — null otherwise.
export const recordItemSorted = (): { total: number; milestone: number | null } => {
    const uid = currentUid();
    if (uid === null) return { total: 0, milestone: null };
    const before = getItemCount();
    const after = before + 1;
    writeStored({ uid, date: todayKey(), count: after });
    const milestone =
        Math.floor(after / MILESTONE_STEP) > Math.floor(before / MILESTONE_STEP)
            ? Math.floor(after / MILESTONE_STEP) * MILESTONE_STEP
            : null;
    return { total: after, milestone };
};
